/**
 * NEW_PLANS_FLAG
 *
 * Centralized feature flag for the new plans UI (PlansCards, UpgradeFeatureModal,
 * UpgradeWindow, UpgradePlanSuccessModal, new account-creation plan/payment pages).
 *
 * Gate: both `beta_tester` AND `beta_tester_internal` user flags must be true.
 *
 * To launch to all users:
 *   1. Search for "isNewPlans" / "isNewPlansEnabled" across the codebase
 *   2. Remove all conditional branches, keeping only the "new plans" code
 *   3. Delete this file
 *   4. Remove legacy account-creation pages (src/views/Auth/AccountCreation/legacy/)
 */

import { computed } from 'vue'
import { useUserStore } from '@/stores/UserStore'

// On localhost we treat every gated feature as enabled, so devs can work on
// flagged surfaces (Orders, the upgraded Sale Pick List, etc.) without the BE
// flag set on their account. Real environments still honour the flag.
export const isLocalhost = () =>
  typeof window !== 'undefined' && window.location?.hostname === 'localhost'

export const useNewPlans = () => {
  const userStore = useUserStore()
  return computed(() => !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal))
}

export const isNewPlansEnabled = () => {
  const userStore = useUserStore()
  return !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal)
}

/**
 * Inventory redesign — the whole rollout, internal testing only.
 * Gate: `user.flags['inventory-updates']` (also accepts `inventory_updates`).
 *
 * The predicate itself lives on `UserStore.hasInventoryUpdates` because
 * `isNewDesign` is AND-ed with it, and this file imports the store (defining it
 * here would be a circular import). This composable is the ergonomic wrapper
 * for components.
 *
 * Gates BOTH halves of the feature:
 *   - the design itself, via `isNewDesign` → every grid / cell / panel already
 *     branching on it, no per-consumer flag check needed;
 *   - the opt-in surfaces that advertise it — the header switch + its confirm
 *     modal, the sidebar promo, the welcome modals, the first-run tour and the
 *     user-menu row. Those are checked explicitly so a classic user never even
 *     sees the invitation.
 */
export const useInventoryUpdates = () => {
  const userStore = useUserStore()
  return computed(() => userStore.hasInventoryUpdates)
}

/**
 * Inventory hits inside GLOBAL SEARCH (CDP-2508) — pilot, by email.
 *
 * The `/search` response can carry an optional `inventory` block (the user's own
 * matching cards), which turns a Card Database row's "Add Card" into
 * "View in Inventory" / "View in Batch". Everyone not on this list keeps the
 * previous behaviour exactly — the store drops the block, so there is nothing
 * for the UI to render and no branch to hit.
 *
 * Deliberately an email allowlist, not a BE flag: this is one reviewer on
 * production, and it needs no backend change to grant or revoke. Swap it for a
 * `user.flags` gate when it graduates past a pilot.
 */
export const SEARCH_INVENTORY_PILOT_EMAILS = ['ryan@collx.app']

export const isSearchInventoryEnabled = () => {
  const userStore = useUserStore()
  const email = userStore.user?.email?.trim().toLowerCase()

  // Localhost sees every gated feature (see `isLocalhost` above) so the surface
  // can be worked on without being the pilot user.
  return isLocalhost() || (!!email && SEARCH_INVENTORY_PILOT_EMAILS.includes(email))
}

export const useSearchInventory = () => computed(() => isSearchInventoryEnabled())

/*
 * Launched, flags removed (2026-08-24):
 *   `bulk-delist` — Bulk End / Relist. Now on for everyone.
 *   `csv-import`  — Inventory CSV import. Now on for everyone; the PLAN
 *                   entitlement (`csv-inventory-import`) still gates it inside
 *                   `InventoryCsvImportModal` and is unrelated to this file.
 */
